import React from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import type { DefaultValues, FieldValues, Path, Resolver } from 'react-hook-form'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useBlocker } from 'react-router-dom'
import styled from 'styled-components'
import type { AnyObjectSchema } from 'yup'

import type { VersionAgnosticBackendErrorResponseData } from '@libs/typescript-utils'
import { isFieldArrayGuard, parseRHFInitialErrors, registerUnsavedForm } from '@libs/typescript-utils'

import type { ReactHookFormExternalSubmitProviderProps } from '@contexts/ReactHookFormExternalSubmitProvider'
import {
  ReactHookFormExternalSubmitProvider,
  useReactHookFormExternalSubmit,
  useReactHookFormExternalSubmitFormRef,
} from '@contexts/ReactHookFormExternalSubmitProvider'

import { SimpleDialog } from '@components/ui/Dialogs'

import { DIALOG_CLASS_NAMES } from '@constants'

import { FormControl } from './FormControl'

const StyledForm = styled.form`
  ${FormControl} {
    margin-top: 6px;
    margin-bottom: 0;
  }
`

type SubmitSuccessFunction<Results> = (response: Results) => void

/** Handed to the resolver on every run, so the form can be asked whether this run should validate at all. */
interface ReactHookFormContext {
  skipValidation?: () => boolean
}

function skippable<FormValues extends FieldValues>(
  resolver: Resolver<FormValues>
): Resolver<FormValues, ReactHookFormContext> {
  return (formValues, context, options) =>
    context?.skipValidation?.() ? { values: formValues, errors: {} } : resolver(formValues, context, options)
}

export interface ReactHookFormProps<FormValues extends FieldValues, Results>
  extends Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  children: React.ReactNode | React.ReactNode[]
  initialErrors?: Nullable<VersionAgnosticBackendErrorResponseData>
  initialValues: DefaultValues<FormValues>
  onSubmit: (values: FormValues, event?: React.BaseSyntheticEvent) => Promise<Results>
  onSubmitFail?: (payload: any) => void
  onSubmitSuccess?: VoidFunction | SubmitSuccessFunction<Results>
  skipResetAfterSuccessfulSubmit?: boolean
  skipUnsavedChanges?: boolean
  skipValidation?: () => boolean
  validationMode?: 'onChange' | 'onSubmit'
  validationSchema?: AnyObjectSchema
  values?: FormValues
}

function BaseReactHookForm<FormValues extends FieldValues, Results>({
  children,
  initialErrors,
  initialValues,
  onSubmit,
  onSubmitFail,
  onSubmitSuccess,
  skipResetAfterSuccessfulSubmit = false,
  skipUnsavedChanges,
  skipValidation,
  validationMode = 'onChange',
  validationSchema,
  values,
  ...rest
}: ReactHookFormProps<FormValues, Results>) {
  const hookFormRef = useReactHookFormExternalSubmitFormRef()
  const triggerSubmit = useReactHookFormExternalSubmit()
  const methods = useForm<FormValues, ReactHookFormContext>({
    mode: validationMode,
    defaultValues: initialValues as DefaultValues<FormValues>,
    values,
    shouldFocusError: false, // This is handled in ReactHookFormSubmitButton
    context: { skipValidation },
    ...(validationSchema ? { resolver: skippable(yupResolver(validationSchema) as Resolver<FormValues>) } : {}),
  })

  const {
    formState: { isSubmitSuccessful, isDirty, isSubmitting },
    reset,
    trigger,
  } = methods

  // unsaved changes prompt dialog when try to navigate away
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !skipUnsavedChanges && isDirty && !isSubmitting && currentLocation.pathname !== nextLocation.pathname
  )
  const showPrompt = blocker.state === 'blocked'

  const guardsNavigation = !skipUnsavedChanges && isDirty && !isSubmitting
  React.useEffect(() => (guardsNavigation ? registerUnsavedForm() : undefined), [guardsNavigation])

  function confirmNavigation() {
    blocker.proceed?.()
  }

  function cancelNavigation() {
    blocker.reset?.()
  }

  //* initial errors
  React.useEffect(() => {
    const parsedInitialErrors = parseRHFInitialErrors(initialErrors)
    if (parsedInitialErrors) {
      Object.keys(parsedInitialErrors).forEach(field => {
        methods.setError(field as Path<FormValues>, {
          type: 'initialError',
          message: parsedInitialErrors[field],
        })
      })
    }
  }, [initialErrors, methods])

  //* NOTE reset must be in useEffect - see docs: https://react-hook-form.com/api/useform/reset
  React.useEffect(() => {
    if (!skipResetAfterSuccessfulSubmit && isSubmitSuccessful) {
      // reset form state with new initialValues
      reset(initialValues)
    }
  }, [skipResetAfterSuccessfulSubmit, isSubmitSuccessful, reset, initialValues])

  async function handleConfirmWithFormSubmit() {
    const result = await trigger()
    if (result) {
      triggerSubmit()
    } else {
      cancelNavigation()
    }
  }

  const formSubmitHandler = methods.handleSubmit(async (values, event) => {
    try {
      const response = await onSubmit(values, event)
      if (showPrompt) {
        confirmNavigation()
      } else {
        onSubmitSuccess?.(response)
      }
    } catch (errors) {
      onSubmitFail?.(errors)
      cancelNavigation()
      if (errors instanceof Error) {
        // unexpected error: error is an exception not a saga reject response
        methods.setError('_error' as Path<FormValues>, {
          type: 'unexpectedError',
          message: errors.message,
        })
      } else if (typeof errors === 'string') {
        // saga reject response as string
        methods.setError('_error' as Path<FormValues>, {
          type: 'submissionError',
          message: errors,
        })
      } else {
        // saga reject response as Record (return type of getFormErrors)
        Object.entries(errors as Record<string, string | Array<Record<string, string>>>).forEach(([field, message]) => {
          if (isFieldArrayGuard(message)) {
            message.forEach((innerField, index) => {
              Object.entries(innerField).forEach(([innerKey, innerMessage]) => {
                methods.setError(`${field}[${index}].${innerKey}` as Path<FormValues>, {
                  type: 'submissionError',
                  message: innerMessage,
                })
              })
            })
          } else {
            methods.setError(field as Path<FormValues>, {
              type: 'submissionError',
              message: message,
            })
          }
        })
      }
    }
  })

  return (
    <FormProvider {...methods}>
      <StyledForm onSubmit={formSubmitHandler} ref={hookFormRef} noValidate autoComplete="off" {...rest}>
        {children}
      </StyledForm>
      <SimpleDialog
        className={DIALOG_CLASS_NAMES.prompt} // TEMP: set minHeight of the dialog
        open={showPrompt}
        onClose={cancelNavigation}
        title={<FormattedMessage id="unsavedChanges.dialog.title" defaultMessage="Nem mentett módosítás" />}
        description={
          <FormattedMessage
            id="unsavedChanges.dialog.description"
            defaultMessage="Még nem mentetted el a módosításaidat. Mit szeretnél tenni?"
          />
        }
        onPrimaryAction={handleConfirmWithFormSubmit}
        onSecondaryAction={confirmNavigation}
        primaryActionText={
          <FormattedMessage id="unsavedChanges.dialog.primaryActionText" defaultMessage="Mentés és tovább" />
        }
        secondaryActionText={
          <FormattedMessage id="unsavedChanges.dialog.secondaryActionText" defaultMessage="Nincs mentés" />
        }
      />
    </FormProvider>
  )
}

interface ReactHookFormExtendedProps<FormValues extends FieldValues, Results>
  extends ReactHookFormProps<FormValues, Results> {
  preventSubmitSuccess?: ReactHookFormExternalSubmitProviderProps['preventSubmitSuccess']
}

export function ReactHookForm<FormValues extends FieldValues, Results>({
  preventSubmitSuccess,
  ...rest
}: ReactHookFormExtendedProps<FormValues, Results>) {
  return (
    <ReactHookFormExternalSubmitProvider preventSubmitSuccess={preventSubmitSuccess}>
      <BaseReactHookForm {...rest} />
    </ReactHookFormExternalSubmitProvider>
  )
}
